import React, { useState } from "react";
import {
    Apple,
    Moon,
    Bath,
    Bell,
    FileText,
    AlertTriangle,
} from "lucide-react";

const wrapStyle = {
    border: "1px solid var(--border, #e5e7eb)",
    borderRadius: 14,
    padding: "12px 14px",
    background: "#fff",
    display: "grid",
    gap: 10,
};

const gridStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
};

const actionBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid var(--border, #e5e7eb)",
    background: "#fff",
    color: "var(--text, #222)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: ".85rem",
};

const modalOverlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 1000,
};

const modalStyle = {
    width: "100%",
    maxWidth: 520,
    borderRadius: 18,
    background: "#fff",
    border: "1px solid var(--border, #e5e7eb)",
    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    padding: 18,
    display: "grid",
    gap: 14,
};

const textareaStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--border, #ddd)",
    background: "#fff",
    color: "var(--text, #222)",
    outline: "none",
    minHeight: 100,
    resize: "vertical",
};

const footerStyle = {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
};

const cancelBtnStyle = {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid var(--border, #ddd)",
    background: "#fff",
    color: "var(--text, #222)",
    cursor: "pointer",
    fontWeight: 700,
};

const saveBtnStyle = {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid transparent",
    background: "var(--accent, #5c4d7d)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 800,
};

const activityOptions = [
    { key: "meal", label: "Log Meal", icon: Apple },
    { key: "nap", label: "Log Nap", icon: Moon },
    { key: "bathroom", label: "Bathroom", icon: Bath },
    { key: "reminder", label: "Add Reminder", icon: Bell },
    { key: "note", label: "Add Note", icon: FileText },
    { key: "incident", label: "Incident", icon: AlertTriangle },
];

const prettyType = (type) => {
    switch (type) {
        case "meal":
            return "Meal";
        case "nap":
            return "Nap";
        case "bathroom":
            return "Bathroom";
        case "reminder":
            return "Reminder";
        case "note":
            return "Note";
        case "incident":
            return "Incident";
        default:
            return "Activity";
    }
};

const StudentActivityLogger = ({ student, onSaveActivity }) => {
    const [activeType, setActiveType] = useState(null);
    const [details, setDetails] = useState("");
    const [saving, setSaving] = useState(false);

    const handleOpen = (type) => {
        setActiveType(type);
        setDetails("");
    };

    const handleClose = () => {
        setActiveType(null);
        setDetails("");
        setSaving(false);
    };

    const handleSave = async () => {
        if (!activeType) return;

        const payload = {
            id: `activity-${Date.now()}`,
            studentId: student?._id,
            type: activeType,
            typeLabel: prettyType(activeType),
            title: `${prettyType(activeType)} logged`,
            description: details.trim(),
            timestamp: new Date().toISOString(),
            timeLabel: "Just now",
            source: "profile-logger",
        };

        try {
            setSaving(true);
            await onSaveActivity?.(payload);
            handleClose();
        } catch (err) {
            console.error("Failed to save student activity:", err);
            setSaving(false);
        }
    };

    return (
        <>
            <div style={wrapStyle}>
                <div style={{ fontWeight: 800, fontSize: "1rem" }}>
                    Student Activity Logger
                </div>

                <div style={gridStyle}>
                    {activityOptions.map((item) => {
                        const Icon = item.icon;

                        return (
                            <button
                                key={item.key}
                                type="button"
                                style={actionBtnStyle}
                                onClick={() => handleOpen(item.key)}
                            >
                                <Icon size={18} />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {activeType && (
                <div style={modalOverlayStyle}>
                    <div style={modalStyle}>
                        <div style={{ fontWeight: 800, fontSize: "1rem" }}>
                            {prettyType(activeType)} Entry
                        </div>

                        <div style={{ color: "var(--text-secondary, #666)", fontSize: ".9rem" }}>
                            Add a quick update for {student?.firstName || "this student"}.
                        </div>

                        <textarea
                            style={textareaStyle}
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            placeholder={`Enter ${prettyType(activeType).toLowerCase()} details...`}
                        />

                        <div style={footerStyle}>
                            <button type="button" style={cancelBtnStyle} onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                style={saveBtnStyle}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? "Saving..." : "Save Entry"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StudentActivityLogger;